import React from 'react'
import PropTypes from 'prop-types'
import Modal from '../shared/Modal'
import Loader from '../shared/Loader'
import ExcelOptions from './components/ExcelOptions'
import PDFChoiceOptions from './components/PDFChoiceOptions'
import PDFOptions from './components/PDFOptions'
import PhotosOptions from './components/PhotosOptions'
import InitialView from './components/InitialView'
import ModalFooter from './components/ModalFooter'
import { useExportModal, VIEWS } from './hooks/useExportModal'
import { singlePdfLimitMessage } from './utils/exportHelpers'

const ExportModal = ({
  answersCount,
  singlePdfMaxAnswers,
  collect,
  draft,
  sections,
  exportUrl,
  pdfExportUrl,
  pdfCustomizeUrl,
  photosExportUrl,
  buttonLabel,
  buttonClassName,
  iconClassName,
  defaultView,
  allowedExports,
  currentSectionId,
  ignoreFilters,
  selectionParam
}) => {
  const {
    isOpen,
    currentView,
    currentStep,
    loading,
    formData,
    handleClose,
    handleOpen,
    handleExcelClick,
    handlePDFClick,
    handlePhotosClick,
    handlePDFCustomize,
    handlePhotosCustomize,
    handleBack,
    handleExcelExport,
    handlePhotosExport,
    handlePDFExport,
    handleSectionChange,
    handleVariablesChange,
    handleExportAsZipChange
  } = useExportModal({
    collect,
    draft,
    exportUrl,
    pdfExportUrl,
    pdfCustomizeUrl,
    photosExportUrl,
    defaultView,
    currentSectionId,
    ignoreFilters,
    selectionParam
  })

  const renderContent = () => {
    if (loading)
      return <Loader text="Veuillez patienter..." size="small" />

    switch (currentView) {
    case VIEWS.INITIAL:
      return (
        <InitialView
          allowedExports={allowedExports}
          onExcelClick={handleExcelClick}
          onPDFClick={handlePDFClick}
          onPhotosClick={handlePhotosClick}
        />
      )
    case VIEWS.EXCEL_OPTIONS:
      return (
        <ExcelOptions
          collect={collect}
          draft={draft}
          ignoreFilters={ignoreFilters}
          selectionParam={selectionParam}
          onExport={handleExcelExport}
        />
      )
    case VIEWS.PDF_CHOICE:
      return (
        <PDFChoiceOptions
          onExportAll={handlePDFExport}
          onCustomize={handlePDFCustomize}
        />
      )
    case VIEWS.PDF_OPTIONS:
      return (
        <PDFOptions
          sections={sections}
          value={formData.sections}
          withVariables={formData.allVariables}
          exportAsZip={formData.exportAsZip}
          singlePdfLimit={singlePdfLimitMessage(answersCount, singlePdfMaxAnswers, 'réponses')}
          onChange={handleSectionChange}
          onVariablesChange={handleVariablesChange}
          onExportAsZipChange={handleExportAsZipChange}
        />
      )
    case VIEWS.PHOTOS_OPTIONS:
      return (
        <PhotosOptions
          currentSectionId={currentSectionId}
          onCustomize={handlePhotosCustomize}
          onExportAll={handlePhotosExport}
        />
      )
    default:
      return null
    }
  }
  const modalTitle = (
    <>
      <i className="fas fa-share-square" />
      <span className="ml-2">
        Exporter les ressources
      </span>
    </>
  )

  return (
    <>
      <button
        type="button"
        className={buttonClassName}
        onClick={handleOpen}
      >
        {iconClassName ? (
          <>
            <i className={iconClassName} />
            {' '}
            {buttonLabel}
          </>
        ) : null}
      </button>
      <Modal
        isOpen={isOpen}
        title={modalTitle}
        footer={
          <ModalFooter
            currentView={currentView}
            currentStep={currentStep}
            loading={loading}
            onBack={handleBack}
            onExcelExport={handleExcelExport}
            onPDFExport={handlePDFExport}
            onPhotosExport={handlePhotosExport}
            onClose={handleClose}
          />
        }
        onClose={handleClose}
      >
        {renderContent()}
      </Modal>
    </>
  )
}

ExportModal.defaultProps = {
  answersCount: null,
  buttonClassName: 'btn btn-primary',
  buttonLabel: 'Exporter',
  currentSectionId: null,
  defaultView: VIEWS.INITIAL,
  draft: null,
  iconClassName: 'fas fa-file-export',
  ignoreFilters: false,
  pdfCustomizeUrl: null,
  pdfExportUrl: null,
  photosExportUrl: null,
  sections: [],
  selectionParam: 'resource_ids',
  singlePdfMaxAnswers: null
}

ExportModal.propTypes = {
  allowedExports: PropTypes.arrayOf(PropTypes.string).isRequired,
  answersCount: PropTypes.number,
  buttonClassName: PropTypes.string,
  buttonLabel: PropTypes.string,
  collect: PropTypes.shape({
    id: PropTypes.number.isRequired
  }).isRequired,
  currentSectionId: PropTypes.number,
  defaultView: PropTypes.string,
  draft: PropTypes.shape({
    id: PropTypes.number.isRequired
  }),
  exportUrl: PropTypes.string.isRequired,
  iconClassName: PropTypes.string,
  ignoreFilters: PropTypes.bool,
  pdfCustomizeUrl: PropTypes.string,
  pdfExportUrl: PropTypes.string,
  photosExportUrl: PropTypes.string,
  sections: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  })),
  selectionParam: PropTypes.string,
  singlePdfMaxAnswers: PropTypes.number
}

export default ExportModal
